import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, ChevronUp, PlusCircle, ClipboardCheck, Shield, AlertTriangle, Crown, Package, XCircle, Flame, Eye } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EVENT_CONFIG = {
  created:       { color: 'bg-green-500',  icon: PlusCircle,     label: 'Created' },
  inspection:    { color: 'bg-blue-500',   icon: ClipboardCheck, label: 'Inspection' },
  treatment:     { color: 'bg-purple-500', icon: Shield,         label: 'Treatment' },
  disease:       { color: 'bg-red-500',    icon: AlertTriangle,  label: 'Disease' },
  requeened:     { color: 'bg-amber-500',  icon: Crown,          label: 'Requeened' },
  harvest:       { color: 'bg-yellow-500', icon: Package,        label: 'Harvest' },
  status_change: { color: 'bg-stone-500',  icon: XCircle,        label: 'Status' },
  destroyed:     { color: 'bg-red-700',    icon: Flame,          label: 'Destroyed' },
};

const HiveTimeline = ({ hiveId }) => {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(true);
  const [filter, setFilter] = useState('all');
  const [showCount, setShowCount] = useState(20);

  useEffect(() => {
    if (!hiveId) return;
    const fetchTimeline = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/hives/${hiveId}/timeline`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        setEvents(res.data.events || []);
      } catch { /* empty */ }
      setLoading(false);
    };
    fetchTimeline();
  }, [hiveId]);

  if (loading) return null;
  if (events.length === 0) return null;

  const filteredEvents = filter === 'all' ? events : events.filter(e => e.type === filter);
  const visibleEvents = filteredEvents.slice(0, showCount);
  const typeCounts = {};
  events.forEach(e => { typeCounts[e.type] = (typeCounts[e.type] || 0) + 1; });

  return (
    <div className="bg-white border border-stone-200 rounded-2xl shadow-sm overflow-hidden" data-testid="hive-timeline">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-5 hover:bg-stone-50 transition"
      >
        <h2 className="text-lg font-bold text-stone-800 flex items-center gap-2">
          <Eye className="h-5 w-5 text-indigo-600" />
          Hive Timeline
          <span className="text-sm font-normal text-stone-400 ml-1">({events.length} events)</span>
        </h2>
        {expanded ? <ChevronUp className="h-5 w-5 text-stone-400" /> : <ChevronDown className="h-5 w-5 text-stone-400" />}
      </button>

      {expanded && (
        <div className="px-5 pb-5">
          {/* Filter pills */}
          <div className="flex flex-wrap gap-1.5 mb-4">
            <button
              onClick={() => setFilter('all')}
              className={`text-xs px-2.5 py-1 rounded-full font-medium transition ${filter === 'all' ? 'bg-indigo-600 text-white' : 'bg-stone-100 text-stone-600 hover:bg-stone-200'}`}
            >
              All ({events.length})
            </button>
            {Object.entries(EVENT_CONFIG).map(([type, cfg]) => {
              const count = typeCounts[type] || 0;
              if (count === 0) return null;
              return (
                <button
                  key={type}
                  onClick={() => setFilter(type)}
                  className={`text-xs px-2.5 py-1 rounded-full font-medium transition ${filter === type ? 'bg-indigo-600 text-white' : 'bg-stone-100 text-stone-600 hover:bg-stone-200'}`}
                >
                  {cfg.label} ({count})
                </button>
              );
            })}
          </div>

          {/* Timeline */}
          <div className="relative ml-3">
            {/* Vertical line */}
            <div className="absolute left-2 top-0 bottom-0 w-0.5 bg-stone-200" />

            {visibleEvents.map((event, idx) => {
              const cfg = EVENT_CONFIG[event.type] || EVENT_CONFIG.inspection;
              const Icon = cfg.icon;
              const dateStr = (event.date || '').split('T')[0];

              return (
                <div key={`${event.type}-${idx}`} className="relative pl-8 pb-4 last:pb-0 group">
                  {/* Dot */}
                  <div className={`absolute left-0 top-1 w-4.5 h-4.5 rounded-full ${cfg.color} flex items-center justify-center ring-2 ring-white`} style={{ width: '18px', height: '18px' }}>
                    <Icon className="h-2.5 w-2.5 text-white" />
                  </div>
                  {/* Content */}
                  <div
                    className={`bg-stone-50 rounded-xl px-3 py-2.5 border border-stone-100 group-hover:border-stone-200 transition ${event.id && event.type === 'inspection' ? 'cursor-pointer hover:bg-indigo-50' : ''}`}
                    onClick={() => { if (event.id && event.type === 'inspection') navigate(`/?view=inspection&inspectionId=${event.id}`); }}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-semibold text-stone-800">{event.title}</span>
                      <span className="text-[11px] text-stone-400 ml-2 flex-shrink-0">{dateStr}</span>
                    </div>
                    {event.detail && (
                      <p className="text-xs text-stone-500 mt-0.5">{event.detail}</p>
                    )}
                    {event.beekeeper && (
                      <p className="text-[11px] text-stone-400 mt-0.5">by {event.beekeeper}</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Show more */}
          {filteredEvents.length > showCount && (
            <button
              onClick={() => setShowCount(prev => prev + 20)}
              className="w-full mt-3 text-xs text-indigo-600 font-medium py-2 hover:bg-indigo-50 rounded-lg transition"
            >
              Show more ({filteredEvents.length - showCount} remaining)
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default HiveTimeline;
